import { State } from "../state";
import { InventoryModState } from "./state";
import { getStoreAccessors } from "typesafe-vuex";
import { IInventoryItem, IInventoryTransactionBatchCreate } from "@/interfaces";
import { api } from "@/api";
import { ActionContext } from 'vuex';
import { commitSetInventoryItems, 
    commitSetInventoryItem, 
    commitRemoveInventoryItem, 
    commitSetSelectedBranchId,
    commitSetTransactionBatch, 
    commitSetTransactions,
    commitAddTransaction,
    commitUpdateTransaction,
    commit} from './mutations';
import { dispatchCheckApiError } from '../main/actions';
import { AxiosError } from "axios";
import { commitAddNotification } from "../main/mutations";

type MainContext = ActionContext<InventoryModState, State>;

export const actions = {
    async getInventoryItems(context: MainContext) {
        if (!context.state.selectedBranchId) {
            return;
        }

        try {
            const response = await api.getInventory(context.rootState.main.token,
                context.state.selectedBranchId);
            if (response) {
                commitSetInventoryItems(context, response.data);
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async getInventoryItem(context: MainContext, id: number) {
        try {
            const response = await api.getInventoryItem(context.rootState.main.token, id);
            if (response) {
                commitSetInventoryItem(context, response.data);
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async addInventoryItem(context: MainContext, payload: IInventoryItem) {
        try {
            const response = await api.addInventoryItem(context.rootState.main.token, payload);
            if (response) {
                commitSetInventoryItem(context, response.data);
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async updateInventoryItem(context: MainContext, payload: { id: number, data: IInventoryItem }) {
        try {
            const response = await api.updateInventoryItem(context.rootState.main.token, 
                payload.id, payload.data);
            if (response) {
                commitSetInventoryItem(context, response.data);
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async removeInventoryItem(context: MainContext, id: number) {
        try {
            const response = await api.deleteInventoryItem(context.rootState.main.token, id);
            if (response) {
                commitRemoveInventoryItem(context, { id });
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async syncInventoryItems(context: MainContext) {
        if (!context.state.selectedBranchId) {
            return;
        }

        try {
            const branchId = context.state.selectedBranchId;
            const response = await api.syncInventory(context.rootState.main.token, branchId);
            if (response) {
                await dispatchGetInventoryItems(context);
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },

    // =========================    Inventory Transactions Batch ==============================
    async createTransactionBatch(context: MainContext, payload: IInventoryTransactionBatchCreate){
        try {
            const response = await api.addInventoryTransactionBatch(context.rootState.main.token, payload);
            if (response) {
                commitSetTransactionBatch(context, response.data);
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async getTransactionBatch(context: MainContext, id: number) {
        try {
            const response = await api.getInventoryTransactionBatch(context.rootState.main.token, id);
            if (response) {
                commitSetTransactionBatch(context, response.data);
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async getLastOpenTransactionBatch(context: MainContext, id: number) {
        try {
            const response = await api.getLastOpenInventoryTransactionBatch(context.rootState.main.token, id);
            if (response) {
                commitSetTransactionBatch(context, response.data);
            }
        } catch (error) {
            if ((error as AxiosError).response?.status === 404) {
                commitSetTransactionBatch(context, null);
            } else {
                await dispatchCheckApiError(context, error);
            }
            
        }
    },
    async closeTransactionBatch(context: MainContext, id: number|null) {
        try {
            const  the_id = context.state.inventoryTransactionBatch?.id;
            const response = await api.closeInventoryTransactionBatch(context.rootState.main.token, the_id!);
            if (response) {
                commitSetTransactionBatch(context, null);
                commitSetTransactions(context, []);
                commitAddNotification(context, { content: "Se han aplicado las modificaciones al inventario", color: "success" });
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async addTransaction(context: MainContext, payload: any) {
        try {
            const response = await api.addInventoryTransaction(context.rootState.main.token, payload);
            if (response) {
                commitAddTransaction(context, response.data);
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    
    async updateTransaction(context: MainContext, payload: {id:number, payload: any}) {
        try {
            const response = await api.updateInventoryTransaction(context.rootState.main.token, payload.id, payload.payload);
            if (response) {
                commitUpdateTransaction(context, response.data);
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async getTransactions(context: MainContext, id: number) {
        try {
            const response = await api.getInventoryTransactions(context.rootState.main.token, id);
            if (response) {
                commitSetTransactions(context, response.data);
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    }

};

export const { dispatch } = getStoreAccessors<InventoryModState, State>("");

export const dispatchGetInventoryItems = dispatch(actions.getInventoryItems);
export const dispatchGetInventoryItem = dispatch(actions.getInventoryItem);
export const dispatchAddInventoryItem = dispatch(actions.addInventoryItem);
export const dispatchUpdateInventoryItem = dispatch(actions.updateInventoryItem);
export const dispatchRemoveInventoryItem = dispatch(actions.removeInventoryItem);
export const dispatchSyncInventoryItems = dispatch(actions.syncInventoryItems);
export const dispatchCreateTransactionBatch = dispatch(actions.createTransactionBatch);
export const dispatchGetTransactionBatch = dispatch(actions.getTransactionBatch);
export const dispatchGetLastOpenTransactionBatch = dispatch(actions.getLastOpenTransactionBatch);
export const dispatchAddTransaction = dispatch(actions.addTransaction);
export const dispatchUpdateTransaction = dispatch(actions.updateTransaction);
export const dispatchGetTransactions = dispatch(actions.getTransactions);
export const dispatchCloseTransactionBatch = dispatch(actions.closeTransactionBatch);
